import * as React from 'react';
import { noop } from 'lodash';
import { Moment } from 'moment';
import { AddressList } from '@qbe/dfc-services';

import { AddressDataType } from 'hooks/useSearchAddress';

import { initialState as initialStateFormData } from './initialState';

enum FormActionType {
  UpdateForm = 'update-form',
  Reset = 'reset',
}

type FormAction = { type: FormActionType.UpdateForm; payload: object } | { type: FormActionType.Reset };

export interface LandingFormFields {
  lodgement: string | undefined;
}

export const YesNoList = ['yes', 'no'] as const;
export type YesNo = (typeof YesNoList)[number];

export const lodgementList = ['Complaint', 'Feedback', 'Compliment', 'Financial hardship application'] as const;
export type Lodgement = (typeof lodgementList)[number];

export const policyClaimList = ['policy', 'claim'] as const;

export const individualOrBusinessList = ['individual', 'business'] as const;
export type IndOrBus = (typeof individualOrBusinessList)[number] | null;

export interface ContactReasonForm {
  policyOrClaim: string | null;
  policyNumber: string | null;
  claimNumber: string | null;
  indOrBus: IndOrBus;
}

export type PreferredMethod = 'email' | 'phone' | 'post';
export type StepMethod = 'Edit' | 'Preview';

export const titleList = ['Mr', 'Ms', 'Miss', 'Mrs', 'Dr', 'Prof'] as const;
export type Title = (typeof titleList)[number];

export const genderList = ['Male', 'Female', 'Non-binary', 'Prefer not to say'] as const;
export type Gender = (typeof genderList)[number];

export const languageList = [
  'Arabic',
  'Cantonese',
  'Greek',
  'Hindi',
  'Italian',
  'Mandarin',
  'Punjabi',
  'Spanish',
  'Vietnamese',
  'Other',
] as const;
export type Language = (typeof languageList)[number];

export const employmentTypeList = ['Casual', 'Contractor', 'Full-time', 'Part-time', 'Self employed'] as const;
export type EmploymentType = (typeof employmentTypeList)[number];

export const incomeTypeList = [
  'Centrelink',
  'Dividends',
  'Investment',
  'Interest',
  'Pension',
  'Rent',
  'Other',
] as const;
export type IncomeType = (typeof incomeTypeList)[number];

export const assistanceList = [
  'Extension of due date for payment',
  'Paying a reduced lump sum',
  'Paying in instalments',
  'Postponing one or more instalments',
  'Other',
] as const;
export type Assistance = (typeof assistanceList)[number];

export type EmploymentData = {
  id: string;
  employmentType: string;
  employerName: string;
  occupation: string;
  contactPerson: string;
  contactPersonNumber: string;
  salary: string | null;
};

export type DependentData = {
  id: string;
  dependentName: string;
  dependentAge: string;
};

export type IncomeData = {
  id: string;
  incomeType: string;
  incomeDescription: string;
  incomeAmount: string | null;
};

export interface PersonalDetails {
  title: Title | null;
  firstname: string;
  lastname: string;
  dob: Date | Moment | null;
  gender: Gender | null;
}

export interface FhaPersonalDetails {
  title: Title | null;
  firstname: string;
  lastname: string;
  dependents: DependentData[];

  isLangInterpreter: string | undefined;
  vulnerableCustomer: string | null;
  language: string | null;
  otherLanguage: string | null;
}

export interface PhysicalAddressFields {
  physicalAddress: string | null;
  physicalAddressObject?: AddressDataType | AddressList | null;
  phyStreetAddress: string | null;
  phySuburb: string | null;
  phyCountry: string | null;
  phyState: string | null;
  phyPostcode: string | null;
  isPhysAddressValid: boolean;
  isManualPhysAddress: boolean;
  isManualPhysAddressValid?: boolean;
}

export interface PostalAddressFields {
  postalAddress: string | null;
  postalAddressObject?: AddressDataType | AddressList | null;
  posStreetAddress: string | null;
  posSuburb: string | null;
  posCountry: string | null;
  posState: string | null;
  posPostcode: string | null;
  isPosAddressValid: boolean;
  isManualPosAddress: boolean;
}

export interface FinRepresentativeFields {
  nominatedRepType: string | null;
  repFirstName: string | null;
  repLastName: string | null;
  repContactMethod: string | null;
  repEmailAddress: string | null;
  repPostalAddress: string | null;
  repPostalAddressObject?: AddressDataType | AddressList | null;
  repPosStreetAddress: string | null;
  repPosSuburb: string | null;
  repPosCountry: string | null;
  repPosState: string | null;
  repPosPostcode: string | null;
  isRepPosAddressValid: boolean;
  isManualRepPosAddress: boolean;
  repMPhoneNumber: string | null;
  repHPhoneNumber: string | null;
  repOPhoneNumber: string | null;
}

export interface ContactDetailsForm extends PhysicalAddressFields, PostalAddressFields, FinRepresentativeFields {
  iContactPreferedMethod: string | null;
  cEmailAddress: string | null;
  addressType: string | null;
  mPhoneNumber: string | null;
  hPhoneNumber: string | null;
  oPhoneNumber: string | null;
}

export interface OtherDetails {
  isLangInterpreter: string | undefined;
  vulnerableCustomer: string | null;
  language: string | null;
  otherLanguage: string | null;
  businessName: string | null;
  isSmallBusiness: string | undefined;
}

export interface ComplaintDetailsFormFields {
  complaintDate: Date | null;
  productType: string | null;
  compDescription: string | null;
  compType: string | undefined;
  compArea: string | null;
  compCategory: string[];
  sensitiveCase: string | null;
  resolved: string | null;
  desiredOutcome: string | null;
  resolvedDate: Date | null;
  outcomeType: string | null;
  outcomeDesc: string | null;
  amountInDispute: string | null;
  amountCompResolved: string | null;
  files?: File[];
}

export interface HardshipFormFields {
  reason: string | null;
  assistance: string | null;
  description: string | null;
}

export interface EmploymentFormFields {
  employmentStatus: string | null;
  employmentData: EmploymentData[];
}

export interface IncomeFormFields {
  incomeStatus: string | null;
  incomeData: IncomeData[];
}

export interface ExpenseFormFields {
  rentMortgage: number;
  childSupport: number;
  otherLoan: number;
  motorVehicle: number;
  creditCard: number;
  livingCosts: number;
  utilities: number;
  schoolFees: number;
  hospitalMedicalCosts: number;
  insurance: number;
  otherExpense: number;
  declaration: boolean;
  files: File[];
}

export interface Step2Fields
  extends ContactReasonForm, PersonalDetails, ContactDetailsForm, OtherDetails, FhaPersonalDetails {}

export type FormDataType = LandingFormFields &
  Step2Fields &
  ComplaintDetailsFormFields &
  EmploymentFormFields &
  IncomeFormFields &
  ExpenseFormFields &
  HardshipFormFields;

interface FormState {
  formData: Partial<FormDataType> | undefined;
}

interface FormProviderProps {
  children: React.ReactNode;
}

interface FormContextProps {
  state: FormState;
  dispatch: (action: FormAction) => void;
}

export const keyNames = Object.keys(initialStateFormData);

const initialState: FormState = {
  formData: initialStateFormData,
};

const FormContext = React.createContext<FormContextProps>({ state: initialState, dispatch: () => noop });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formReducer: React.Reducer<any, any> = (state, action) => {
  switch (action.type) {
    case FormActionType.UpdateForm:
      return { ...state, formData: { ...state.formData, ...action.payload } };
    case FormActionType.Reset:
      return initialState;
    default:
      throw new Error('Unhandled action type');
  }
};

const FormProvider = ({ children }: FormProviderProps) => {
  const [state, dispatch] = React.useReducer(formReducer, initialState);
  return <FormContext.Provider value={{ state, dispatch }}>{children}</FormContext.Provider>;
};

const useFormState = () => {
  const context = React.useContext(FormContext);
  if (!context) {
    throw new Error(`useForm must be used within a FormProvider`);
  }
  const {
    state: { formData },
    dispatch,
  } = context;

  const { lodgement } = formData || {};

  const updateForm = async (newFormData: Partial<FormDataType>) => {
    return dispatch({ type: FormActionType.UpdateForm, payload: newFormData });
  };

  const resetForm = () => dispatch({ type: FormActionType.Reset });
  const COMPLAINT = 'Complaint';
  const COMPLIMENT = 'Compliment';
  const FEEDBACK = 'Feedback';
  const FINANCIAL_HARDSHIP = 'Financial hardship application';
  const isFNH = lodgement === FINANCIAL_HARDSHIP;

  const [subjectPrefix, setSubjectPrefix] = React.useState<LandingFormFields['lodgement']>('');

  const initialErrorMessage = 'Something has gone wrong. We apologise for any inconvenience.';

  const genericErrorMessage = `${initialErrorMessage} Please try again later or give us a call on 1300 650 503.`;
  const fhaErrorMessage = `${initialErrorMessage} Please try again later or give us a call on 1300 902 417.`;

  const [defaultErrorMessage, setDefaultErrorMessage] = React.useState<string>(genericErrorMessage);

  React.useEffect(() => {
    let newLodgement = undefined;
    setDefaultErrorMessage(genericErrorMessage);
    switch (lodgement) {
      case COMPLAINT:
        newLodgement = 'CNT';
        break;
      case COMPLIMENT:
        newLodgement = 'CMT';
        break;
      case FEEDBACK:
        newLodgement = 'FBK';
        break;
      case FINANCIAL_HARDSHIP:
        newLodgement = 'FNH';
        setDefaultErrorMessage(fhaErrorMessage);
        break;

      default:
        newLodgement = undefined;
    }

    setSubjectPrefix(newLodgement);
  }, [fhaErrorMessage, genericErrorMessage, lodgement]);

  return {
    defaultErrorMessage,
    isFNH,
    formData,
    updateForm,
    resetForm,
    subjectPrefix,
  };
};

export { FormProvider, useFormState };
